import React, { useState } from "react";
import SiteLayout from "../components/layout/SiteLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../lib/settings";
import { Mail, MapPin, Phone, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const { settings } = useSettings();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Message sent. We'll be in touch within a few days.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <SiteLayout>
      <section className="container-x py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">{settings.contact_eyebrow}</div>
            <h1 className="display text-5xl lg:text-7xl">{settings.contact_headline}</h1>
            <p className="mt-6 max-w-2xl text-[hsl(var(--ink-muted))] leading-relaxed">
              {settings.contact_intro}
            </p>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <div className="eyebrow mb-2">Sunday Gatherings</div>
                <div className="display text-2xl">{settings.sunday_service_1} · {settings.sunday_service_2}</div>
                <div className="text-sm text-[hsl(var(--ink-muted))] mt-1">Children's program runs at both services.</div>
              </div>
              <div>
                <div className="eyebrow mb-2">{settings.midweek_label}</div>
                <div className="display text-2xl">{settings.midweek_time}</div>
                <div className="text-sm text-[hsl(var(--ink-muted))] mt-1">{settings.midweek_location} — open to all.</div>
              </div>
            </div>

            <div className="mt-12 space-y-3 text-sm">
              <div className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 text-[hsl(var(--terracotta))]" /> {settings.address_line1}{settings.address_line2 && <>, {settings.address_line2}</>}</div>
              {settings.email && <div className="flex items-start gap-3"><Mail size={16} className="mt-0.5 text-[hsl(var(--terracotta))]" /> {settings.email}</div>}
              {settings.phone && <div className="flex items-start gap-3"><Phone size={16} className="mt-0.5 text-[hsl(var(--terracotta))]" /> {settings.phone}</div>}
              {settings.office_hours && <div className="flex items-start gap-3"><Clock size={16} className="mt-0.5 text-[hsl(var(--terracotta))]" /> Office hours · {settings.office_hours}</div>}
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-5 bg-[hsl(var(--sand))] border border-[hsl(var(--line))] p-8 lg:p-10 rounded-sm space-y-5"
          >
            <div className="display text-3xl">Send us a note.</div>
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Name</label>
              <input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Email</label>
              <input
                data-testid="contact-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                type="email"
                className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Message</label>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
              />
            </div>
            <button
              data-testid="contact-submit"
              disabled={busy}
              className="lift inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta-deep))] text-[hsl(var(--linen))] text-sm tracking-wide disabled:opacity-60"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              Send message
            </button>
            {sent && (
              <div data-testid="contact-success" className="text-sm text-[hsl(var(--moss))]">
                Thanks — your note arrived. A pastor will reply within a few days.
              </div>
            )}
          </form>
        </div>
      </section>
    </SiteLayout>
  );
}
